/**
 * iconos.js — un solo lugar donde se decide qué dibujo representa cada cosa.
 *
 * Por qué existe: la app usaba emojis (✅ ⚠️ ⏻ 🏷️) esparcidos por el código.
 * Los emojis se ven distintos en cada sistema —Windows, Android e iPhone
 * dibujan el suyo—, no heredan el color del texto y no se pueden alinear
 * bien. Aquí se usa Lucide: trazo fino, tamaño uniforme, y toman el color
 * de donde estén.
 *
 * Se nombran por SIGNIFICADO, no por dibujo: `Iconos.cortado`, no
 * `Iconos.circuloRojo`. Si mañana el corte se representa con otra figura,
 * se cambia aquí y lo heredan las veinte pantallas.
 *
 *     import { Iconos } from "../componentes/base/iconos";
 *     <Iconos.cliente size={18} />
 */

import {
  LuActivity, LuAntenna, LuArrowLeft, LuArrowRight, LuBan, LuBell,
  LuBuilding2, LuCalendar, LuChartLine, LuCheck, LuChevronDown,
  LuChevronRight, LuCircleAlert, LuCircleCheck, LuCircleHelp, LuCircleX,
  LuClock, LuCreditCard, LuDatabase, LuDownload, LuEraser, LuFileText,
  LuFilter, LuHouse, LuImport, LuInfo, LuLayoutDashboard, LuListChecks,
  LuLoader, LuLogOut, LuMapPin, LuMessageSquare, LuMinus, LuNetwork,
  LuMoon, LuPencil, LuPhone, LuPlus, LuPower, LuPrinter, LuRefreshCw,
  LuRotateCcw,
  LuRouter, LuScissors, LuSearch, LuSettings, LuSkipBack, LuSkipForward,
  LuSun, LuTag, LuTrash2, LuTriangleAlert, LuUser, LuUserCog, LuUsers, LuWifi,
  LuWifiOff, LuX, LuZap,
} from "react-icons/lu";

export const Iconos = {
  // --- Navegación / secciones ---
  panel: LuLayoutDashboard,
  clientes: LuUsers,
  cliente: LuUser,
  pagos: LuCreditCard,
  caja: LuCreditCard,
  cortes: LuBan,
  planes: LuTag,
  soporte: LuMessageSquare,
  respaldos: LuDatabase,
  equipos: LuRouter,
  antena: LuAntenna,
  ajustes: LuSettings,
  salir: LuLogOut,
  inicio: LuHouse,
  historial: LuChartLine,
  empresa: LuBuilding2,
  usuarios: LuUserCog,       // usuarios del sistema, distinto de `clientes`
  onus: LuListChecks,        // ONUs ya registradas
  importar: LuImport,
  olt: LuNetwork,
  tijeras: LuScissors,       // el corte como acción, no como estado

  // --- Estado del servicio ---
  activo: LuCircleCheck,
  suspendido: LuTriangleAlert,
  cortado: LuCircleX,
  cancelado: LuMinus,
  enLinea: LuWifi,
  fueraDeLinea: LuWifiOff,

  // --- Acciones ---
  agregar: LuPlus,
  editar: LuPencil,
  eliminar: LuTrash2,
  buscar: LuSearch,
  filtrar: LuFilter,
  actualizar: LuRefreshCw,
  imprimir: LuPrinter,
  descargar: LuDownload,
  encender: LuPower,
  reiniciar: LuRotateCcw,
  restablecer: LuEraser,     // dejar de fábrica: se borra lo configurado
  cerrar: LuX,
  confirmar: LuCheck,

  // --- Avisos ---
  exito: LuCircleCheck,
  aviso: LuTriangleAlert,
  error: LuCircleAlert,
  info: LuInfo,
  duda: LuCircleHelp,
  cargando: LuLoader,

  // --- Datos ---
  telefono: LuPhone,
  ubicacion: LuMapPin,
  fecha: LuCalendar,
  hora: LuClock,
  documento: LuFileText,
  senal: LuActivity,
  potencia: LuZap,
  notificacion: LuBell,

  // --- Tema ---
  dia: LuSun,
  noche: LuMoon,

  // --- Flechas ---
  atras: LuArrowLeft,
  adelante: LuArrowRight,
  desplegar: LuChevronDown,
  siguiente: LuChevronRight,
  primera: LuSkipBack,       // paginación: saltar al principio
  ultima: LuSkipForward,     // paginación: saltar al final
};

/** El icono que corresponde al estado de un cliente. */
export function iconoDeEstado(estado) {
  const mapa = {
    activo: Iconos.activo,
    suspendido: Iconos.suspendido,
    cortado: Iconos.cortado,
    cancelado: Iconos.cancelado,
  };
  return mapa[String(estado || "").toLowerCase()] || Iconos.duda;
}
